import { FilterRule } from '@src/components/filter/types';
import { useGetAllFlowInstances } from '../../utils/queryHooks';
import { get } from 'lodash';
import { useMemo, useState } from 'react';
import { SessionTimelineTableItem } from '@conviva/organism-components';
import { useFlowConfigs } from '@src/hooks/flow/useFlowConfigs';

export const useSessionsWithFlowInstances = ({
  viewerId,
  clientId,
  interval,
  filter,
  sessions,
  enabled = true,
}: {
  viewerId: string[];
  clientId: string;
  interval: string;
  filter?: FilterRule[][];
  sessions?: SessionTimelineTableItem[];
  enabled?: boolean;
  selectedFlowIdentifiers?: string[];
}) => {
  const [selectedFlowIdentifiers, setSelectedFlowIdentifiers] = useState<string[]>([]);

  const { data, error, isLoading, refetch } = useGetAllFlowInstances({
    viewerId,
    clientId,
    interval,
    filter,
    enabled,
  });

  const flowConfigs = useFlowConfigs();
  const resArr = get(data, '0.data.0.results', []);

  const { flowToSessions, sessionToFlows, flowOptions } = useMemo(() => {
    if (!resArr || !sessions || resArr.length === 0 || sessions.length === 0) {
      return { flowToSessions: {}, sessionToFlows: {}, flowOptions: [] };
    }

    const flowToSessions: Record<string, Record<string, string[]>> = {};
    const sessionToFlows: Record<string, any[]> = {};
    const uniqueFlows = new Map<string, { identifier: string; name: string }>();

    resArr.forEach((flow) => {
      const { flow_identifier, flow_start_timestamp, flow_end_timestamp } = flow;

      if (!flow_identifier || !flow_start_timestamp || !flow_end_timestamp) {
        return;
      }

      const flowMetadata = Object.values(flowConfigs).find(
        (config) => config.identifier === flow_identifier,
      );
      const flowName = flowMetadata?.name || flow_identifier;
      const flowStartEvent = flowMetadata?.definition.events.start_event.name;
      const flowEndEvent = flowMetadata?.definition.events.success_event.name;
      const flowStartTime = new Date(flow_start_timestamp).getTime();
      const flowEndTime = new Date(flow_end_timestamp).getTime();
      const flowTimeRange = `${flow_start_timestamp}-${flow_end_timestamp}`;

      // Store unique flows for options
      if (!uniqueFlows.has(flow_identifier)) {
        uniqueFlows.set(flow_identifier, {
          identifier: flow_identifier,
          name: flowName,
        });
      }

      if (!flowToSessions[flow_identifier]) {
        flowToSessions[flow_identifier] = {};
      }
      if (!flowToSessions[flow_identifier][flowTimeRange]) {
        flowToSessions[flow_identifier][flowTimeRange] = [];
      }

      sessions.forEach((session) => {
        const sessionStartTime = new Date(session.eventTimeStampStart).getTime();
        const sessionEndTime = new Date(session.eventTimeStampEnd).getTime();
        const hasOverlap = flowStartTime <= sessionEndTime && flowEndTime >= sessionStartTime;

        if (hasOverlap) {
          if (!flowToSessions[flow_identifier][flowTimeRange].includes(session.sessionId)) {
            flowToSessions[flow_identifier][flowTimeRange].push(session.sessionId);
          }

          if (!sessionToFlows[session.sessionId]) {
            sessionToFlows[session.sessionId] = [];
          }
          sessionToFlows[session.sessionId].push({
            ...flow,
            flow_name: flowName,
            flow_start_event: flowStartEvent,
            flow_end_event: flowEndEvent,
          });
        }
      });
    });

    // Filter based on selectedFlowIdentifiers
    let filteredFlowToSessions: Record<string, Record<string, string[]>> = {};
    let filteredSessionToFlows: Record<string, any[]> = {};

    if (selectedFlowIdentifiers.length === 0) {
      // If no flows selected, return all data
      filteredFlowToSessions = flowToSessions;
      filteredSessionToFlows = sessionToFlows;
    } else {
      // Filter flowToSessions based on selected flows
      selectedFlowIdentifiers.forEach((flowIdentifier) => {
        if (flowToSessions[flowIdentifier]) {
          filteredFlowToSessions[flowIdentifier] = flowToSessions[flowIdentifier];
        }
      });

      // Filter sessionToFlows based on selected flows
      Object.entries(sessionToFlows).forEach(([sessionId, sessionFlows]) => {
        const filteredFlows = sessionFlows.filter((flow) =>
          selectedFlowIdentifiers.includes(flow.flow_identifier),
        );
        if (filteredFlows.length > 0) {
          filteredSessionToFlows[sessionId] = filteredFlows;
        }
      });
    }

    const flowOptions = Array.from(uniqueFlows.values()).map((flow) => ({
      label: flow.name,
      value: flow.identifier,
    }));

    return {
      flowToSessions: filteredFlowToSessions,
      sessionToFlows: filteredSessionToFlows,
      flowOptions,
    };
  }, [resArr, sessions, flowConfigs, selectedFlowIdentifiers]);

  return {
    data: resArr,
    error,
    isLoading,
    refetch,
    flowToSessions,
    sessionToFlows,
    flowOptions,
    selectedFlowIdentifiers,
    setSelectedFlowIdentifiers,
  };
};
